import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Camera, Hand } from "lucide-react";
import { Link } from "react-router-dom";
import { P_PROCESS_STEPS, type PProcessRow } from "@hisense/shared";
import { PageBody } from "@/components/ui/PageHeader";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { QueryErrorState } from "@/components/ui/States";
import { usePageCrumbs } from "@/components/shell/breadcrumb";
import { ExportButton } from "@/components/domain/ExportButton";
import { useMarkPProcess, usePProcess } from "@/api/hooks";
import { useCan } from "@/app/access";
import { getLocalizedError } from "@/lib/api";
import { SeqBar, StateMark } from "@/components/process/ProcessMarks";
import { PhotoSlot, PhotoToggle, usePProcessPhotos } from "@/components/process/PProcessPhotos";

/** Nấc nào gồm những bước nào — dựng một lần từ khai báo dùng chung. */
const SEQS = [...new Set(P_PROCESS_STEPS.map((s) => s.seq))].sort((a, b) => a - b);

export function PProcessPage() {
  const { t } = useTranslation();
  const query = usePProcess();
  const mark = useMarkPProcess();
  const can = useCan();
  const [open, setOpen] = useState<string | null>(null);
  // Ô ảnh đang mở: `${dòng}|${nấc}` — mỗi lúc một ô, cho gọn.
  const [slot, setSlot] = useState<string | null>(null);
  usePageCrumbs([{ label: t("pProcess.title") }]);

  const rows = query.data?.rows ?? [];
  const label = (code: string) => t(`pProcess.steps.${code}`);

  return (
    <PageBody>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">{t("pProcess.title")}</h1>
          <p className="mt-1 max-w-3xl text-sm text-ink-soft">{t("pProcess.intro")}</p>
          {/* Từ 29/10/2026 màn này chỉ còn đơn bật P; đơn còn lại ở màn Quy trình thường. */}
          <Link to="/order-process" className="mt-1 inline-block text-sm text-brand hover:underline">{t("orderProcess.normalLink")}</Link>
        </div>
        {/*
          MỘT CỘT CHO MỖI BƯỚC trong chuỗi 13 bước, không gộp thành một chuỗi
          chữ: câu hỏi người ta mang bảng này đi hỏi là "đơn nào kẹt ở bước
          nào", và câu đó chỉ lọc được khi mỗi bước là một cột riêng.
        */}
        <ExportButton<PProcessRow>
          name="p-process"
          title={t("pProcess.title")}
          rows={rows}
          disabled={query.isFetching}
          errorKey="pProcess.error"
          rowKey={(r) => r.piId}
          columns={[
            { header: t("pProcess.piNo"), value: (r) => r.piNo },
            { header: t("pProcess.customer"), value: (r) => r.customerName },
            { header: t("pProcess.mo"), value: (r) => r.moNo },
            { header: t("pProcess.lots"), value: (r) => r.lotNumbers.join(" · ") },
            { header: t("pProcess.current"), value: (r) => (r.currentStep ? label(r.currentStep) : null) },
            { header: t("pProcess.progress"), value: (r) => `${r.doneCount}/${r.totalCount}` },
            { header: t("pProcess.complete"), value: (r) => t(r.complete ? "common.yes" : "common.no") },
            ...P_PROCESS_STEPS.map((step) => ({
              header: label(step.code),
              value: (r: PProcessRow) => {
                // Ba trạng thái, không phải hai: "chưa tới" và "không áp
                // dụng cho đơn này" là hai chuyện khác nhau, gộp thành ô trống
                // là mất đúng thông tin người ta cần.
                const state = r.steps.find((x) => x.code === step.code);
                return state ? t(`pProcess.states.${state.state}`) : null;
              },
            })),
          ]}
        />
      </div>

      {((query.data?.unlinked.orders ?? 0) > 0 || (query.data?.unlinked.purchaseOrders ?? 0) > 0) && (
        <p className="mb-4 flex items-start gap-2 rounded-md border border-warn/40 bg-warn/10 p-3 text-sm">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warn" aria-hidden />
          <span>
            {(query.data?.unlinked.orders ?? 0) > 0 && (
              <span className="block font-medium text-ink">
                {t("pProcess.unlinked", { count: query.data?.unlinked.orders ?? 0 })}
              </span>
            )}
            {(query.data?.unlinked.purchaseOrders ?? 0) > 0 && (
              <span className="block font-medium text-ink">
                {t("pProcess.unlinkedPo", { count: query.data?.unlinked.purchaseOrders ?? 0 })}
              </span>
            )}
            <span className="block text-xs text-ink-soft">{t("pProcess.unlinkedHint")}</span>
          </span>
        </p>
      )}

      {query.error ? (
        <QueryErrorState
          error={query.error}
          message={getLocalizedError(query.error, t, "pProcess.error")}
          forbiddenMessage={t("errors.forbidden")}
          onRetry={() => void query.refetch()}
        />
      ) : rows.length === 0 && !query.isLoading ? (
        <p className="rounded-md border border-line bg-surface p-6 text-center text-sm text-ink-soft">
          {t("pProcess.empty")}
        </p>
      ) : (
        <div className="space-y-3">
          {rows.map((row: PProcessRow) => {
            const key = `${row.piId}:${row.moId ?? ""}`;
            const expanded = open === key;
            const byCode = Object.fromEntries(row.steps.map((s) => [s.code, s]));
            return (
              <div key={key} className="rounded-md border border-line bg-surface">
                <button
                  type="button"
                  className="flex w-full flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-left"
                  onClick={() => setOpen(expanded ? null : key)}
                  aria-expanded={expanded}
                >
                  <span className="font-mono tabular text-sm font-medium text-ink">{row.piNo}</span>
                  <span className="text-sm text-ink-soft">{row.customerName ?? "—"}</span>
                  <span className="font-mono tabular text-xs text-ink-faint">
                    {row.moNo ?? t("pProcess.noMo")}
                  </span>
                  {row.lotNumbers.length > 0 && (
                    <span className="font-mono tabular text-xs text-ink-faint">
                      {t("pProcess.lots")}: {row.lotNumbers.join(", ")}
                    </span>
                  )}

                  <SeqBar seqs={SEQS} steps={row.steps} />
                  <span className="font-mono tabular text-xs text-ink-soft">
                    {row.doneCount}/{row.totalCount}
                  </span>
                  {row.complete ? (
                    <Badge tone="ok">{t("pProcess.complete")}</Badge>
                  ) : (
                    <Badge tone="warn" outline>
                      {t("pProcess.current")}: {row.currentStep ? label(row.currentStep) : "—"}
                    </Badge>
                  )}
                  {row.outOfOrder.length > 0 && (
                    <Badge tone="danger" outline>
                      <AlertTriangle className="mr-1 inline size-3" aria-hidden />
                      {t("pProcess.outOfOrder")}
                    </Badge>
                  )}
                  {row.photoCount > 0 && (
                    <span className="flex items-center gap-1 text-xs text-ink-soft" title={t("pProcess.photos.count", { count: row.photoCount })}>
                      <Camera aria-hidden="true" className="size-3.5" />
                      <span className="font-mono tabular">{row.photoCount}</span>
                    </span>
                  )}
                </button>

                {expanded && (
                  <div className="border-t border-line px-4 py-3">
                    <div className="mb-3 flex flex-wrap gap-2 text-xs">
                      <Badge tone="neutral" outline>
                        {t(row.purchased ? "pProcess.purchased" : "pProcess.inStock")}
                      </Badge>
                      <Badge tone="neutral" outline>
                        {t(row.subcontracted ? "pProcess.subcontracted" : "pProcess.inHouse")}
                      </Badge>
                    </div>

                    {row.outOfOrder.length > 0 && (
                      <p className="mb-3 rounded-md border border-danger/40 bg-danger/5 p-2 text-xs text-ink-soft">
                        {t("pProcess.outOfOrderHint")}
                      </p>
                    )}

                    <ol className="space-y-1">
                      {SEQS.map((seq) => (
                        <Fragment key={seq}>
                          {P_PROCESS_STEPS.filter((s) => s.seq === seq).map((step) => {
                            const state = byCode[step.code];
                            if (!state) return null;
                            const isManual = step.evidence === "manual";
                            const done = state.state === "done";
                            return (
                              <li
                                key={step.code}
                                className="flex flex-wrap items-center gap-2 border-b border-line/60 py-1.5 last:border-b-0"
                              >
                                <span className="flex w-6 justify-center"><StateMark state={state.state} /></span>
                                <span className="font-mono tabular text-xs text-ink-faint">{seq}</span>
                                <span className={state.state === "notApplicable" ? "text-sm text-ink-faint" : state.state === "failed" ? "text-sm font-semibold text-danger" : state.state === "concession" ? "text-sm font-semibold text-warn" : "text-sm text-ink"}>
                                  {label(step.code)}
                                </span>
                                {state.state === "failed" && (
                                  <Badge tone="danger" className="text-[10px]">{t("pProcess.failed")}</Badge>
                                )}
                                {state.state === "concession" && (
                                  <Badge tone="warn" className="text-[10px]" title={t("pProcess.concessionHint")}>
                                    {t("pProcess.concession")}
                                  </Badge>
                                )}
                                {step.testNo && (
                                  <Badge tone="info" outline className="text-[10px]">
                                    {t("pProcess.test", { n: step.testNo })}
                                  </Badge>
                                )}
                                {isManual && (
                                  <Badge tone="brass" outline className="text-[10px]" title={t("pProcess.manualHint")}>
                                    <Hand className="mr-1 inline size-3" aria-hidden />
                                    {t("pProcess.manual")}
                                  </Badge>
                                )}
                                <span className="ml-auto text-xs text-ink-faint">
                                  {t(`pProcess.states.${state.state}`)}
                                </span>
                                {isManual && state.state !== "notApplicable" && can("records:update") && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    disabled={mark.isPending}
                                    onClick={() =>
                                      mark.mutate({
                                        piId: row.piId,
                                        moId: row.moId,
                                        step: step.code,
                                        remove: done,
                                      })
                                    }
                                  >
                                    {t(done ? "pProcess.markUndo" : "pProcess.markDone")}
                                  </Button>
                                )}
                                <StepPhotoToggle piId={row.piId} step={step.code} open={slot === `${key}|${step.code}`} onToggle={() => setSlot(slot === `${key}|${step.code}` ? null : `${key}|${step.code}`)} />
                                {slot === `${key}|${step.code}` && <PhotoSlot piId={row.piId} step={step.code} />}
                              </li>
                            );
                          })}
                        </Fragment>
                      ))}
                    </ol>
                    <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line/60 pt-3">
                      <span className="text-sm text-ink">{t("pProcess.photos.general")}</span>
                      <StepPhotoToggle label piId={row.piId} step="general" open={slot === `${key}|general`} onToggle={() => setSlot(slot === `${key}|general` ? null : `${key}|general`)} />
                      {slot === `${key}|general` && <PhotoSlot piId={row.piId} step="general" />}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </PageBody>
  );
}

/** Nút "Ảnh" của một nấc: số ảnh lấy từ bộ nhớ đệm khi PI đã được mở ảnh (danh sách ảnh nằm sau cổng mật khẩu, không tự tải). */
function StepPhotoToggle({ piId, step, open, onToggle, label }: { piId: string; step: string; open: boolean; onToggle: () => void; label?: boolean }) {
  const cached = usePProcessPhotos(piId, false).data;
  const count = !cached ? 0 : step === "piIssued" ? (cached.orderEvidence?.length ?? 0) : cached.attachments.filter((a) => a.step === step).length;
  return <PhotoToggle count={count} open={open} onClick={onToggle} label={label} />;
}
